import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Box, Button, Flex, Spinner, Text, Textarea, useToast } from '@chakra-ui/react';
import { FiClock } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import type { MarketplaceOrderListItemDto } from '@foodfluence/shared';
import { lcApi } from '../../../services/api/lc';
import { ff } from '../../../theme';
import { Pill } from '../../../components/ui';
import { useMarketplaceOrders } from '../../../contexts/MarketplaceOrdersContext';
import {
  formatMarketplaceOrderDateLong,
  listMarketplaceDeliverables,
  marketplaceOrderBundleTitle,
  marketplaceOrderInspirationLinks,
  marketplaceOrderInspirationAssetUrls,
  marketplaceOrderInviteExpiryTimeLeftLabel,
  marketplaceOrderStatusPill,
} from '../utils/marketplaceOrderUtils';
import { CounterMarketplaceOrderSheet } from '../components/CounterMarketplaceOrderSheet';
import { DeclineMarketplaceOrderSheet } from '../components/DeclineMarketplaceOrderSheet';
import { MarketplaceOrderBriefAccordionItems } from '../components/MarketplaceOrderBriefAccordion';
import { MarketplaceOrderStickyHeader } from '../components/MarketplaceOrderStickyHeader';

export default function MarketplaceOrderAcceptPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { refetch: refetchMarketplaceOrders } = useMarketplaceOrders();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<'accept' | 'decline' | null>(null);
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [extensionMessage, setExtensionMessage] = useState('');
  const [declineSheetOpen, setDeclineSheetOpen] = useState(false);
  const [counterSheetOpen, setCounterSheetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<MarketplaceOrderListItemDto & { orderBrief?: any } | null>(null);

  useEffect(() => {
    if (!orderId) {
      setError('Missing order id.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    lcApi
      .getMarketplaceOrder(orderId)
      .then((res) => {
        if (cancelled) return;
        setOrder(res.data);
      })
      .catch((e) => {
        console.error('Error loading marketplace order', e);
        if (!cancelled) setError('Could not load order.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const brandName = order?.brandName?.trim() || 'Brand';
  const avatarLetter = (brandName[0] ?? 'B').toUpperCase();
  const pill = useMemo(() => (order ? marketplaceOrderStatusPill(order.status) : null), [order]);
  const deliverables = useMemo(
    () => listMarketplaceDeliverables(order?.deliverables ?? null),
    [order?.deliverables]
  );

  const acceptanceCountdown = useMemo(
    () => marketplaceOrderInviteExpiryTimeLeftLabel(order?.inviteExpiresAt?.seconds ?? null),
    [order?.inviteExpiresAt?.seconds]
  );

  const runAction = async (kind: 'accept' | 'decline') => {
    if (!orderId) return;
    setActionLoading(kind);
    try {
      const res = kind === 'accept' ? await lcApi.acceptMarketplaceOrder(orderId) : await lcApi.declineMarketplaceOrder(orderId);
      setOrder(res.data);
      void refetchMarketplaceOrders();
      toast({
        title: kind === 'accept' ? 'Order accepted' : 'Order declined',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      if (kind === 'accept') {
        navigate(`/collabs/marketplace-orders/${orderId}/submission`);
      } else {
        navigate('/collabs');
      }
    } catch (e) {
      console.error(`Error on ${kind} marketplace order`, e);
      const fallback = kind === 'accept' ? 'Could not accept order.' : 'Could not decline order.';
      toast({
        title: 'Something went wrong',
        description: e instanceof Error && e.message ? e.message : fallback,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const canCounter = order?.status === 'pendingCreatorAcceptance' && !order?.brandRejectedCounter;
  const canEditCounter = order?.status === 'creatorCountered';

  useEffect(() => {
    if (orderId && order?.status === 'pendingCompletion') {
      navigate(`/collabs/marketplace-orders/${orderId}/submission`, { replace: true });
    }
  }, [navigate, orderId, order?.status]);

  if (loading) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white">
        <Spinner size="lg" color={ff.ink} thickness="3px" />
        <Text fontSize="14px" color={ff.text2}>
          Loading order...
        </Text>
      </Flex>
    );
  }

  if (error || !order) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white" px={6}>
        <Text textStyle="display" fontSize="22px" color={ff.ink}>
          Could not load order
        </Text>
        <Text fontSize="14px" color={ff.text2} textAlign="center">
          {error ?? 'Order not found.'}
        </Text>
        <Button variant="inkOutline" h="48px" px={6} onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Flex>
    );
  }

  const isPendingAcceptance = order.status === 'pendingCreatorAcceptance';
  const isCountered = order.status === 'creatorCountered';
  const isInviteExpired = order.status === 'inviteExpired';
  const extensionPending = order.extensionRequest?.status === 'pending';
  const idealTimeline = formatMarketplaceOrderDateLong(order.orderBrief?.logistics?.contentDeliveryTargetDate ?? null);

  const handleRequestExtension = async () => {
    if (!orderId || extensionPending || extensionLoading) return;
    setExtensionLoading(true);
    try {
      const res = await lcApi.requestInviteExtension(orderId, {
        message: extensionMessage.trim() || undefined,
      });
      setOrder(res.data);
      toast({
        title: 'Extension request sent',
        description: 'The brand will review whether they can re-open this invite.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (e) {
      console.error('requestInviteExtension', e);
      toast({
        title: 'Could not send request',
        description: 'Try again later or contact support.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setExtensionLoading(false);
    }
  };

  if (!isPendingAcceptance && !isCountered && !isInviteExpired) return null;

  return (
    <Flex direction="column" h="100dvh" minH="100svh" bg="white" overflow="hidden">
      <Box flexShrink={0}>
      <MarketplaceOrderStickyHeader
        onBack={() => navigate(-1)}
        avatarLetter={avatarLetter}
        brandName={brandName}
        instagramHandle={order?.brandInstagramHandle}
        trailing={
          isInviteExpired && pill ? (
            <Box textAlign="right">
              <Pill tone="ink">{pill.label}</Pill>
              <Text mt={1} fontSize="12px" fontWeight="500" color={ff.ink}>
                Marketplace order
              </Text>
            </Box>
          ) : pill ? (
            <Box textAlign="right">
              <Pill tone="ink">
                <Box as={FiClock} boxSize="12px" mr="6px" />
                {acceptanceCountdown ?? '—'}
              </Pill>
              <Text mt={1} fontSize="12px" fontWeight="500" color={ff.ink}>
                To accept
              </Text>
            </Box>
          ) : undefined
        }
      />
      </Box>

      <Box flex="1" minH={0} overflowY="auto" pb={6}>
        {isCountered && (
          <Box mx={4} mt={4} p={4} bg={ff.limeSurface} borderRadius="20px" borderWidth="1px" borderColor={ff.line}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>Counter offer pending</Text>
            <Text fontSize="13px" color={ff.body} mt={1}>
              Your counter (${order.creatorProposedCash}) is awaiting the brand's response. You can update it below before they respond.
            </Text>
          </Box>
        )}
        {order?.brandCounteredBack && (
          <Box mx={4} mt={4} p={4} bg={ff.cream} borderRadius="20px" borderWidth="1px" borderColor={ff.line}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>Brand countered your offer</Text>
            <Text fontSize="13px" color={ff.body} mt={1}>
              The brand responded with new terms below. You can accept, decline, or counter again.
              {order.brandCounterReason ? ` Their note: "${order.brandCounterReason}"` : ''}
            </Text>
          </Box>
        )}
        {order?.brandRejectedCounter && (
          <Box mx={4} mt={4} p={4} bg={ff.grey} borderRadius="20px" borderWidth="1px" borderColor={ff.line}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>Brand declined your counter</Text>
            <Text fontSize="13px" color={ff.body} mt={1}>
              The brand rejected your counter offer. You can still accept or decline their original offer below.
            </Text>
          </Box>
        )}
        {isInviteExpired && (
          <Box mx={4} mt={4} p={4} bg={ff.cream} borderRadius="20px" borderWidth="1px" borderColor={ff.line}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>Accept window ended</Text>
            <Text fontSize="13px" color={ff.body} mt={1}>
              You can ask the brand to extend the invite so you can still accept or negotiate.
            </Text>
            {extensionPending ? (
              <Text fontSize="15px" fontWeight="600" color={ff.ink} mt={3}>
                Extension requested — the brand will review and may re-open this order.
              </Text>
            ) : (
              <>
                <Text fontSize="14px" fontWeight="600" color={ff.ink} mt={4} mb="6px">
                  Message to brand (optional)
                </Text>
                <Textarea
                  value={extensionMessage}
                  onChange={(e) => setExtensionMessage(e.target.value)}
                  placeholder="Add context for why you still want this partnership..."
                  rows={3}
                  bg="white"
                  resize="none"
                />
              </>
            )}
          </Box>
        )}
        <Accordion allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
          <MarketplaceOrderBriefAccordionItems
            model={{
              deliverables,
              bundleTitle: marketplaceOrderBundleTitle(deliverables),
              cash: order.cash,
              narrative: order.orderBrief?.narrative,
              contentBriefFile: (order.orderBrief as { requirements?: { contentBriefFile?: string } })?.requirements
                ?.contentBriefFile,
              fulfillmentType: order.orderBrief?.logistics?.fulfillmentType,
              idealTimelineDisplay: idealTimeline,
              inspirationLinks: marketplaceOrderInspirationLinks(order.orderBrief, order.deliverables),
              inspirationAssetUrls: marketplaceOrderInspirationAssetUrls(order.orderBrief),
              otherNotes: (order.orderBrief as { requirements?: { otherNotes?: string } })?.requirements?.otherNotes,
            }}
          />
        </Accordion>
      </Box>

      <Box
        flexShrink={0}
        bg="white"
        borderTop="1px solid"
        borderColor={ff.line}
        px={4}
        pt={3}
        pb="calc(12px + env(safe-area-inset-bottom, 0px))"
      >
        {isInviteExpired ? (
          <Button
            variant="lime"
            w="100%"
            isDisabled={extensionPending || extensionLoading}
            isLoading={extensionLoading}
            onClick={handleRequestExtension}
          >
            {extensionPending ? 'Extension requested' : 'Request invite extension'}
          </Button>
        ) : (
          <>
            <Flex gap={3} mb={(canCounter || canEditCounter) ? 2 : 0}>
              <Button
                variant="inkOutline"
                flex="0 0 calc(40% - 6px)"
                isDisabled={!isPendingAcceptance || actionLoading != null}
                onClick={() => setDeclineSheetOpen(true)}
              >
                Decline
              </Button>
              <Button
                variant="lime"
                flex={1}
                isDisabled={!isPendingAcceptance || actionLoading != null}
                isLoading={actionLoading === 'accept'}
                onClick={() => runAction('accept')}
              >
                Accept order
              </Button>
            </Flex>
            {(canCounter || canEditCounter) && (
              <Button
                w="100%"
                h="44px"
                variant="ghost"
                fontSize="14px"
                fontWeight="600"
                color={ff.ink}
                textDecoration="underline"
                onClick={() => setCounterSheetOpen(true)}
                isDisabled={actionLoading != null}
                _hover={{ bg: ff.grey }}
              >
                {canEditCounter ? 'Edit counter offer' : 'Send counter offer'}
              </Button>
            )}
          </>
        )}
      </Box>

      <DeclineMarketplaceOrderSheet
        isOpen={declineSheetOpen}
        onClose={() => setDeclineSheetOpen(false)}
        isSubmitting={actionLoading === 'decline'}
        onConfirmDecline={async (_payload) => {
          await runAction('decline');
        }}
      />

      {order && orderId && (canCounter || canEditCounter) && (
        <CounterMarketplaceOrderSheet
          isOpen={counterSheetOpen}
          onClose={() => setCounterSheetOpen(false)}
          order={order}
          orderId={orderId}
          onSuccess={(updated) => {
            setOrder(updated);
            void refetchMarketplaceOrders();
          }}
        />
      )}
    </Flex>
  );
}

